/** Display preferences that belong to this browser - the ones My settings offers that no endpoint
 * stores (ADR-0212).
 *
 * **The Advanced Scope 2 view** (decision 5): by default one Scope 2 number is shown everywhere
 * (location-based), and the market-based column and its basis chip appear on their own once an
 * instrument is recorded in the period. This switch forces them on for a person who wants them
 * without one. It is a **display preference and not a statement about what any law requires**: a
 * receipt always names the method it used (rule 3), a filing states its basis explicitly at issue,
 * and market-based is calculated and kept for every period that has it whatever this says.
 *
 * **Stored per viewer, on this device, and nothing more** - the same trade `theme.ts` makes, for the
 * same reason: `localStorage` is a convenience that throws in a private window and with site data
 * blocked, so every access is inside a `try`, "off" is what a failed read means (the default a
 * first-time visitor sees), and a failed write means the choice lasts until the tab closes. A
 * per-person field on the profile is the plan's shape (`docs/design/user-flow-ia-2026-11.md` section
 * 12) and needs a migration; until it exists the preference is this browser's, and My settings says
 * so.
 *
 * `useAdvancedScope2` is for the screen that draws the Scope 2 table: it reads the switch and
 * re-renders when it changes, in this tab or another.
 */

import { useSyncExternalStore } from "react";

export const ADVANCED_SCOPE2_KEY = "emitiq.advancedScope2";

/** Fired on this window when the switch changes, so a screen in the same tab hears it (the browser's
 * own `storage` event only fires in the other tabs). */
const CHANGED = "emitiq:advanced-scope2";

export function readAdvancedScope2(): boolean {
  try {
    return window.localStorage.getItem(ADVANCED_SCOPE2_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeAdvancedScope2(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(ADVANCED_SCOPE2_KEY, "1");
    else window.localStorage.removeItem(ADVANCED_SCOPE2_KEY);
  } catch {
    // Private mode or blocked storage: the choice lasts until the tab closes.
  }
  window.dispatchEvent(new Event(CHANGED));
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CHANGED, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGED, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** Whether the Advanced Scope 2 view is on, and the setter. */
export function useAdvancedScope2(): readonly [boolean, (on: boolean) => void] {
  const on = useSyncExternalStore(subscribe, readAdvancedScope2, () => false);
  return [on, writeAdvancedScope2] as const;
}
